import React, { useState, useEffect } from 'react';
import { X, FileText, Image, Code, Database, MessageSquare, FileSpreadsheet, Globe, ExternalLink } from 'lucide-react';

const TYPE_CONFIG = {
  javascript: { icon: Code, color: '#f7df1e', label: 'JS' },
  html: { icon: Code, color: '#e34c26', label: 'HTML' },
  markdown: { icon: FileText, color: '#083fa1', label: 'MD' },
  sql: { icon: Database, color: '#336791', label: 'SQL' },
  csv: { icon: FileSpreadsheet, color: '#16a34a', label: 'CSV' },
  text: { icon: FileText, color: '#6b7280', label: 'TXT' },
  image: { icon: Image, color: '#8b5cf6', label: 'IMG' },
  url: { icon: Globe, color: '#10b981', label: 'URL' },
};

const styles = {
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    padding: '8px 0',
    animation: 'pasteChipSlideUp 0.25s ease-out',
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 10px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    color: 'var(--text)',
    transition: 'all 0.2s ease',
    maxWidth: 280,
  },
  chipProject: {
    borderColor: 'rgba(29, 78, 216, 0.4)',
    background: 'rgba(29, 78, 216, 0.06)',
  },
  thumbnail: {
    width: 42,
    height: 42,
    borderRadius: 6,
    objectFit: 'cover',
    flexShrink: 0,
    border: '1px solid var(--card-border)',
  },
  typeIcon: {
    flexShrink: 0,
    opacity: 0.85,
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 140,
  },
  size: {
    color: 'var(--text-secondary)',
    fontSize: 11,
    flexShrink: 0,
  },
  destToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid var(--card-border)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    transition: 'all 0.15s ease',
    flexShrink: 0,
  },
  destToggleProject: {
    borderColor: 'rgba(29, 78, 216, 0.4)',
    color: '#3b82f6',
    background: 'rgba(29, 78, 216, 0.08)',
  },
  removeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.15s ease',
    padding: 0,
  },
};

const cssAnimation = `
@keyframes pasteChipSlideUp {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

// ─── Chip URL enrichi ───────────────────────────────────────────
const UrlChip = ({ att, onRemove }) => {
  const [ogTitle, setOgTitle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('oracle_token');
    fetch(`/api/url/preview?url=${encodeURIComponent(att.content)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.title) setOgTitle(data.title); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [att.content]);

  let domain = att.content;
  try { domain = new URL(att.content).hostname; } catch {}

  return (
    <div style={{
      ...styles.chip,
      borderColor: 'rgba(16, 185, 129, 0.4)',
      background: 'rgba(16, 185, 129, 0.06)',
    }}>
      <Globe size={14} style={{ ...styles.typeIcon, color: '#10b981' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden', flex: 1 }}>
        <span style={{ ...styles.name, maxWidth: 200, fontSize: 12, fontWeight: 500 }} title={att.content}>
          {ogTitle || domain}
        </span>
        {ogTitle && (
          <span style={{ fontSize: 10, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {domain}
          </span>
        )}
      </div>
      {loading && (
        <div style={{ width: 10, height: 10, border: '1.5px solid #10b981', borderTop: '1.5px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
      )}
      <button style={styles.removeBtn} onClick={() => onRemove(att.id)}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
        title="Retirer"
      >
        <X size={11} />
      </button>
    </div>
  );
};

export default function PasteAttachments({ attachments, onRemove, onDestinationChange, showDestinationToggle = true }) {
  if (!attachments || attachments.length === 0) return null;

  return (
    <>
      <style>{cssAnimation}{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={styles.container}>
        {attachments.map((att) => {
          // URL → chip enrichi dédié
          if (att.isUrl) {
            return <UrlChip key={att.id} att={att} onRemove={onRemove} />;
          }

          const config = TYPE_CONFIG[att.type] || TYPE_CONFIG.text;
          const IconComp = config.icon;
          const isProject = att.destination === 'project';

          return (
            <div
              key={att.id}
              style={{ ...styles.chip, ...(isProject ? styles.chipProject : {}) }}
            >
              {/* Thumbnail ou icone */}
              {att.isImage && att.blobUrl ? (
                <img src={att.blobUrl} alt={att.name} style={styles.thumbnail} />
              ) : (
                <IconComp size={14} style={{ ...styles.typeIcon, color: config.color }} />
              )}

              {/* Nom fichier */}
              <span style={styles.name} title={att.name}>{att.name}</span>

              {/* Taille */}
              <span style={styles.size}>{att.sizeLabel}</span>

              {/* Toggle destination */}
              {showDestinationToggle && onDestinationChange && (
                <button
                  style={{ ...styles.destToggle, ...(isProject ? styles.destToggleProject : {}) }}
                  onClick={() => onDestinationChange(att.id, isProject ? 'discussion' : 'project')}
                  title={isProject ? 'Base documentaire — cliquer pour basculer en discussion' : 'Discussion seulement — cliquer pour sauvegarder en base'}
                >
                  {isProject ? <Database size={10} /> : <MessageSquare size={10} />}
                  {isProject ? 'Doc' : 'Chat'}
                </button>
              )}

              {/* Bouton supprimer */}
              <button
                style={styles.removeBtn}
                onClick={() => onRemove(att.id)}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                title="Retirer"
              >
                <X size={11} />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}
